import React, { useState, useCallback } from 'react';
import {
  choirMembers as choirMembersApi,
  choirSchedules as choirSchedulesApi,
  choirAssets as choirAssetsApi
} from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate, formatTime, todayISO } from '../../utils/format';
import { printableRows, printReference } from '../../utils/printRows';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, PrintButton, Drawer
} from '../../components/ui';
import ChoirSummary from './ChoirSummary';

const ASSET_STATUSES = [
  { value: 'Available', label: 'Available / In Library' },
  { value: 'Issued', label: 'Issued to Member' },
  { value: 'Maintenance', label: 'Under Maintenance' },
  { value: 'Lost/Damaged', label: 'Lost / Damaged' }
];

const EMPTY_MEMBER = { name: '', voiceType: '', role: '', status: 'Active', phone: '', joinedDate: '', notes: '' };
const EMPTY_SCHEDULE = { eventType: '', date: '', time: '', conductor: '', headcount: '', songs: '', notes: '' };
const EMPTY_ASSET = { title: '', category: '', status: 'Available', assignedTo: '', notes: '' };

/** The three sibling resources behind this screen. */
type ChoirTab = 'members' | 'schedules' | 'assets';
/** The Summary tab reads all three; the rest of the screen works on one. */
type ChoirView = ChoirTab | 'summary';

const ChoirManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<ChoirView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: ChoirTab = isSummary ? 'members' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [schedForm, setSchedForm] = useState(EMPTY_SCHEDULE);
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET);
  const [editingId, setEditingId] = useState(null);
  /** The record open in the side panel — every field, not just the table's columns. */
  const [viewing, setViewing] = useState<Record<string, any> | null>(null);

  const isEditing = editingId !== null;
  const isDeletedView = viewMode === 'deleted';

  const { notification, notify, dismiss } = useNotification();

  // Each resource stores its live/void flag on a different column.
  const ledger = useMultiLedger({
    members: { resource: choirMembersApi, label: 'Choir Member', prefix: 'CHR', statusField: 'memberStatus' },
    schedules: { resource: choirSchedulesApi, label: 'Schedule', prefix: 'SCH' },
    assets: { resource: choirAssetsApi, label: 'Asset', prefix: 'AST', statusField: 'assetStatus' }
  }, { notify });

  const voiceTypes = useOptionList('church_chr_voice');
  const roles = useOptionList('church_chr_roles');
  const eventTypes = useOptionList('church_chr_events');
  const assetCategories = useOptionList('church_chr_asscat');

  const dataset = isDeletedView ? ledger.voided(activeTab) : ledger.active(activeTab);

  const getRowDate = useCallback((r) => r.date || r.joinedDate || r.dateAdded, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'name', 'title', 'eventType', 'conductor', 'category', 'phone']
  );
  const page = usePagination(filtered);

  const resetForms = () => {
    setMemberForm(EMPTY_MEMBER);
    setSchedForm(EMPTY_SCHEDULE);
    setAssetForm(EMPTY_ASSET);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewing(null);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: its tab (Deleted when it is
  // gone), then its details.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref);
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as ChoirTab;
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) setViewMode('deleted');
    setViewing(row);
  }, !ledger.loading);

  const startEdit = (item) => {
    if (activeTab === 'members') {
      setMemberForm({
        name: item.name || '', voiceType: item.voiceType || '', role: item.role || '',
        status: item.memberStatus || 'Active', phone: item.phone || '',
        joinedDate: item.joinedDate || '', notes: item.notes || ''
      });
    } else if (activeTab === 'schedules') {
      setSchedForm({
        eventType: item.eventType || '', date: item.date || '', time: item.time || '',
        conductor: item.conductor || '', headcount: item.headcount || '',
        songs: item.songs || '', notes: item.notes || ''
      });
    } else {
      setAssetForm({
        title: item.title || '', category: item.category || '', status: item.assetStatus || 'Available',
        assignedTo: item.assignedTo || '', notes: item.notes || ''
      });
    }
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const existingRecordId = (key) =>
    isEditing
      ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string } | undefined)?.recordId
      : null;

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    const voiceType = memberForm.voiceType.trim();
    const role = memberForm.role.trim();
    if (!voiceType || !role) { notify('Voice Type and Role are required.', 'error'); return; }

    if (memberForm.phone && !/^\d{10}$/.test(memberForm.phone)) {
      notify('Mobile number must be exactly 10 digits.', 'error');
      return;
    }
    if (memberForm.phone) {
      const duplicate = ledger.active('members').some((m) => m.phone === memberForm.phone && m.rawId !== editingId);
      if (duplicate) { notify('A member with this mobile number is already registered.', 'error'); return; }
    }

    voiceTypes.ensure(voiceType);
    roles.ensure(role);

    const payload = {
      ...memberForm,
      voiceType,
      role,
      memberStatus: memberForm.status,
      recordId: existingRecordId('members') || ledger.nextSequence('members'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('members', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    const eventType = schedForm.eventType.trim();
    if (!eventType) { notify('Event Type is required.', 'error'); return; }
    eventTypes.ensure(eventType);

    const payload = {
      ...schedForm,
      eventType,
      recordId: existingRecordId('schedules') || ledger.nextSequence('schedules'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('schedules', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    const category = assetForm.category.trim();
    if (!category) { notify('Category is required.', 'error'); return; }
    assetCategories.ensure(category);

    const existing = isEditing ? ledger.active('assets').find((a) => a.rawId === editingId) : null;
    const payload = {
      ...assetForm,
      category,
      assetStatus: assetForm.status,
      recordId: existing?.recordId || ledger.nextSequence('assets'),
      dateAdded: existing?.dateAdded || todayISO(),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('assets', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const sub = { fontSize: '12px', color: t.textFaint };
  const idColumn = { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> };

  const columnsByTab = {
    members: [
      idColumn,
      {
        key: 'name',
        header: 'Member Name',
        render: (r) => (<><strong>{r.name}</strong><br /><span style={sub}>Joined: {formatDate(r.joinedDate)}</span></>)
      },
      { key: 'voiceType', header: 'Voice / Instrument', render: (r) => <Badge>{r.voiceType}</Badge> },
      {
        key: 'role',
        header: 'Role & Status',
        render: (r) => (
          <>
            <div><strong>{r.role}</strong></div>
            <Badge tone={r.memberStatus === 'Active' ? 'success' : 'neutral'}>{r.memberStatus || 'Active'}</Badge>
          </>
        )
      },
      { key: 'phone', header: 'Phone', render: (r) => r.phone || '-' },
      // Captured on the form but never shown in the table (CHM-034).
      { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' }
    ],
    schedules: [
      idColumn,
      {
        key: 'date',
        header: 'Date & Time',
        nowrap: true,
        render: (r) => (<><strong>{formatDate(r.date)}</strong><br /><span style={sub}>{r.time}</span></>)
      },
      { key: 'eventType', header: 'Event Type', render: (r) => <Badge tone="neutral">{r.eventType}</Badge> },
      { key: 'conductor', header: 'Conductor' },
      { key: 'songs', header: 'Songs Planned' }
    ],
    assets: [
      idColumn,
      { key: 'title', header: 'Item / Title', render: (r) => <strong>{r.title}</strong> },
      { key: 'category', header: 'Category', render: (r) => <Badge tone="neutral">{r.category}</Badge> },
      {
        key: 'status',
        header: 'Status & Assignment',
        render: (r) => (
          <>
            <Badge tone={r.assetStatus === 'Available' ? 'success' : r.assetStatus === 'Lost/Damaged' ? 'error' : 'warning'}>
              {r.assetStatus || 'Available'}
            </Badge>
            {r.assignedTo && <div style={{ fontSize: '12px', marginTop: '4px' }}>To: <strong>{r.assignedTo}</strong></div>}
          </>
        )
      },
      { key: 'notes', header: 'Details', wrap: true, render: (r) => r.notes || '-' }
    ]
  };

  const viewButton = (r) => (
    <Button variant="ghost" size="sm" title="View all details" aria-label="View all details" onClick={() => setViewing(r)}>
      👁️
    </Button>
  );

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        { key: 'view', header: '', align: 'right', render: (r) => viewButton(r) }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          render: (r) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              {viewButton(r)}
              <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
              <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
            </div>
          )
        }
      ];

  const columns = [...columnsByTab[activeTab], { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }, ...tailColumns];

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🎵"
        title="Choir Management"
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'members', label: '🎤 Choir Members' },
          { key: 'schedules', label: '📅 Schedules & Attendance' },
          { key: 'assets', label: '📚 Library & Assets' }
        ]}
      />

      {isSummary && (
        <ChoirSummary
          members={ledger.active('members')}
          schedules={ledger.active('schedules')}
          assets={ledger.active('assets')}
          loading={ledger.loading}
          isDarkMode={isDarkMode}
        />
      )}

      {!isSummary && (
      <SubTabs
        value={viewMode}
        onChange={switchView}
        tabs={[
          { key: 'history', label: '📋 View Active Records' },
          { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
          { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
        ]}
      />

      )}

      {!isSummary && (viewMode === 'history' || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Name, Title, Event, Conductor or ID..."
            actions={(
              <>
                <PrintButton
                  module="Choir Management"
                  documentType={(({ members: { doc: 'CHOIR_REGISTER', title: 'Choir Members' }, schedules: { doc: 'CHOIR_SCHEDULE_REGISTER', title: 'Choir Schedules & Attendance' }, assets: { doc: 'CHOIR_ASSET_REGISTER', title: 'Choir Library & Assets' } })[activeTab]).doc}
                  size="sm"
                  title={(({ members: { doc: 'CHOIR_REGISTER', title: 'Choir Members' }, schedules: { doc: 'CHOIR_SCHEDULE_REGISTER', title: 'Choir Schedules & Attendance' }, assets: { doc: 'CHOIR_ASSET_REGISTER', title: 'Choir Library & Assets' } })[activeTab]).title.replace(/[^A-Za-z0-9]+/g, '-')}
                  // Each tab prints its own register, with every row that matches the
                  // filters — dates formatted and Created By on every line.
                  data={() => ({
                    title: `${(({ members: { doc: 'CHOIR_REGISTER', title: 'Choir Members' }, schedules: { doc: 'CHOIR_SCHEDULE_REGISTER', title: 'Choir Schedules & Attendance' }, assets: { doc: 'CHOIR_ASSET_REGISTER', title: 'Choir Library & Assets' } })[activeTab]).title}${isDeletedView ? ' (Deleted)' : ''}`,
                    reference: printReference(searchTerm && `Search: "${searchTerm}"`),
                    rowCount: filtered.length,
                    rows: printableRows(filtered)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                <DateRangeFilter {...range} onChange={page.reset} />
              </>
            )}
          />

          <TablePanel
            title={<>Choir Roster</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              loading={ledger.loading}
              emptyMessage="No records found for this category."
          />
        </>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'members' && (
        <FormLayout
          title={isEditing ? 'Edit Choir Member' : 'Register Choir Member'}
          submitLabel={isEditing ? 'Update Choir Member' : 'Save Member'}
          submitting={ledger.saving}
          onSubmit={handleMemberSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <Input
              label="Member Name" required placeholder="Full name"
              value={memberForm.name}
              onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
            />
            <SearchableSelect
              label="Voice Type / Instrument" required placeholder="Search or type..."
              value={memberForm.voiceType}
              onChange={(v) => setMemberForm((prev) => ({ ...prev, voiceType: v }))}
              options={voiceTypes.options} onOptionsChange={voiceTypes.setOptions}
            />
            <SearchableSelect
              label="Choir Role" required placeholder="Search or type role..."
              value={memberForm.role}
              onChange={(v) => setMemberForm((prev) => ({ ...prev, role: v }))}
              options={roles.options} onOptionsChange={roles.setOptions}
            />
            <Select
              label="Member Status" placeholder=""
              options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive / Leave' }]}
              value={memberForm.status}
              onChange={(e) => setMemberForm({ ...memberForm, status: e.target.value })}
            />
            <Input
              label="Contact Number (10 Digits)" maxLength={10} placeholder="e.g. 9876543210"
              value={memberForm.phone}
              onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Date Joined" type="date"
              value={memberForm.joinedDate}
              onChange={(e) => setMemberForm({ ...memberForm, joinedDate: e.target.value })}
            />
            <Input
              label="Additional Notes" type="textarea" maxLength={500} rows={2} fullWidth
              placeholder="e.g. Also plays Violin, available for morning services..."
              value={memberForm.notes}
              onChange={(e) => setMemberForm({ ...memberForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'schedules' && (
        <FormLayout
          title={isEditing ? 'Edit Event' : 'Schedule Practice / Service'}
          submitLabel={isEditing ? 'Update Schedule' : 'Save Schedule'}
          submitting={ledger.saving}
          onSubmit={handleScheduleSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Event Type" required fullWidth
              placeholder="e.g. Weekly Practice, Sunday Service..."
              value={schedForm.eventType}
              onChange={(v) => setSchedForm((prev) => ({ ...prev, eventType: v }))}
              options={eventTypes.options} onOptionsChange={eventTypes.setOptions}
            />
            <Input
              label="Date" type="date" required
              value={schedForm.date}
              onChange={(e) => setSchedForm({ ...schedForm, date: e.target.value })}
            />
            <Input
              label="Time / Session" required placeholder="e.g. 8:30 AM, Evening Service"
              value={schedForm.time}
              onChange={(e) => setSchedForm({ ...schedForm, time: e.target.value })}
            />
            <Input
              label="Assigned Conductor" required placeholder="Name of conductor"
              value={schedForm.conductor}
              onChange={(e) => setSchedForm({ ...schedForm, conductor: e.target.value })}
            />
            <Input
              label="Headcount / Attendance" type="number" placeholder="Total members present"
              value={schedForm.headcount}
              onChange={(e) => setSchedForm({ ...schedForm, headcount: e.target.value })}
            />
            <Input
              label="Songs to Practice / Sing" type="textarea" maxLength={500} rows={3} required fullWidth
              placeholder="e.g. 1. Amazing Grace, 2. How Great Thou Art..."
              value={schedForm.songs}
              onChange={(e) => setSchedForm({ ...schedForm, songs: e.target.value })}
            />
            <Input
              label="Event Notes" fullWidth
              placeholder="e.g. Need extra mics, wearing blue robes..."
              value={schedForm.notes}
              onChange={(e) => setSchedForm({ ...schedForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'assets' && (
        <FormLayout
          title={isEditing ? 'Edit Item' : 'Register Library/Asset Item'}
          submitLabel={isEditing ? 'Update Item' : 'Save Item'}
          submitting={ledger.saving}
          onSubmit={handleAssetSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <Input
              label="Item Name / Song Title" required fullWidth
              placeholder="e.g. Yamaha Keyboard, Amazing Grace Sheet Music, Blue Robe (L)"
              value={assetForm.title}
              onChange={(e) => setAssetForm({ ...assetForm, title: e.target.value })}
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            />
            <SearchableSelect
              label="Category" required placeholder="Search or type category..."
              value={assetForm.category}
              onChange={(v) => setAssetForm((prev) => ({ ...prev, category: v }))}
              options={assetCategories.options} onOptionsChange={assetCategories.setOptions}
            />
            <Select
              label="Current Status" placeholder=""
              options={ASSET_STATUSES}
              value={assetForm.status}
              onChange={(e) => setAssetForm({ ...assetForm, status: e.target.value })}
            />
            <Input
              label="Assigned/Issued To" placeholder="Member Name (If applicable)"
              value={assetForm.assignedTo}
              onChange={(e) => setAssetForm({ ...assetForm, assignedTo: e.target.value })}
            />
            <Input
              label="Details / Condition" placeholder="e.g. Needs tuning, Book #45, Size Large"
              value={assetForm.notes}
              onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {/* Every field of one record — the table only has room for a summary. */}
      {viewing && (
        <Drawer
          open
          title={activeTab === 'schedules'
            ? `${viewing.eventType || 'Schedule'} — ${formatDate(viewing.date)}`
            : activeTab === 'assets' ? viewing.title || viewing.recordId : viewing.name || viewing.recordId}
          subtitle={`${viewing.recordId || ''} · ${activeTab === 'members' ? 'Choir Member' : activeTab === 'schedules' ? 'Schedule & Attendance' : 'Library & Assets'}`}
          onClose={() => setViewing(null)}
          isDarkMode={isDarkMode}
          footer={!isDeletedView && (
            <Button write fullWidth onClick={() => { const r = viewing; setViewing(null); startEdit(r); }}>✎ Edit</Button>
          )}
        >
          {activeTab === 'schedules' && (
            <>
              <Drawer.Section title="Schedule" />
              <Drawer.Row label="Event Type">
                <Badge tone="neutral">{viewing.eventType || '-'}</Badge>
              </Drawer.Row>
              <Drawer.Row label="Date" value={formatDate(viewing.date)} />
              <Drawer.Row label="Time" value={formatTime(viewing.time)} />
              <Drawer.Row label="Conductor" value={viewing.conductor || '-'} />
              <Drawer.Section title="Attendance" />
              <Drawer.Row label="Singers Present" value={viewing.headcount || '-'} />
              <Drawer.Section title="Songs Planned" />
              <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.songs || '-'}</p>
              <Drawer.Section title="Notes" />
              <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.notes || '-'}</p>
            </>
          )}
          {activeTab === 'members' && (
            <>
              <Drawer.Section title="Member" />
              <Drawer.Row label="Name" value={viewing.name} />
              <Drawer.Row label="Voice / Instrument" value={viewing.voiceType || '-'} />
              <Drawer.Row label="Role" value={viewing.role || '-'} />
              <Drawer.Row label="Status">
                <Badge tone={viewing.memberStatus === 'Active' ? 'success' : 'neutral'}>{viewing.memberStatus || 'Active'}</Badge>
              </Drawer.Row>
              <Drawer.Row label="Phone" value={viewing.phone || '-'} />
              <Drawer.Row label="Date Joined" value={formatDate(viewing.joinedDate)} />
              <Drawer.Section title="Notes" />
              <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.notes || '-'}</p>
            </>
          )}
          {activeTab === 'assets' && (
            <>
              <Drawer.Section title="Item" />
              <Drawer.Row label="Item / Title" value={viewing.title} />
              <Drawer.Row label="Category" value={viewing.category || '-'} />
              <Drawer.Row label="Status">
                <Badge tone={viewing.assetStatus === 'Available' ? 'success' : viewing.assetStatus === 'Lost/Damaged' ? 'error' : 'warning'}>
                  {viewing.assetStatus || 'Available'}
                </Badge>
              </Drawer.Row>
              <Drawer.Row label="Assigned / Issued To" value={viewing.assignedTo || '-'} />
              <Drawer.Row label="Date Added" value={formatDate(viewing.dateAdded)} />
              <Drawer.Section title="Details / Condition" />
              <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.notes || '-'}</p>
            </>
          )}
          <Drawer.Section title="Record" />
          <Drawer.Row label="Created By" value={viewing.createdBy || '-'} />
          {isDeletedView && (
            <>
              <Drawer.Row label="Deleted On" value={viewing.deletedAt || '-'} />
              <Drawer.Row label="Delete Reason" value={viewing.deleteReason || '-'} />
            </>
          )}
        </Drawer>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default ChoirManagement;
